/**
 * FIV-499 follow-up (see docs/superpowers/specs/2026-07-26-fiv-499-consent-region-defaults-design.md):
 * EEA/UK/CH visitors need prior opt-in (ePrivacy/GDPR); everyone else is an
 * opt-out regime (CCPA/CPRA et al.), so trackers may default on there.
 * Timezone is a heuristic — deliberately over-inclusive (all Europe/* zones,
 * including non-EEA Turkey/Russia/Serbia) and strict on any failure. Google's
 * region-scoped Consent Mode defaults in loader.ts back this up for GA when
 * the heuristic misclassifies a real EEA visitor (VPN, traveler).
 */
export type ConsentRegion = "opt-in" | "opt-out";

// EEA/UK/CH IANA zones that don't start with "Europe/".
const OPT_IN_TZ_EXTRAS = new Set([
  "Atlantic/Canary", // ES
  "Atlantic/Madeira", // PT
  "Atlantic/Azores", // PT
  "Atlantic/Reykjavik", // IS
  "Atlantic/Faroe", // DK (Faroe Islands)
  "Atlantic/Faeroe", // legacy alias of the above
  "Asia/Nicosia", // CY
  "Asia/Famagusta", // CY
  "Africa/Ceuta", // ES
  "Arctic/Longyearbyen", // NO (Svalbard)
  // French outermost regions — GDPR fully applies (review finding 7/26):
  "America/Guadeloupe", // GP
  "America/Martinique", // MQ
  "America/Cayenne", // GF (French Guiana)
  "America/Marigot", // MF (Saint-Martin)
  "Indian/Reunion", // RE
  "Indian/Mayotte", // YT
]);

export function regionFromTimeZone(tz: string | undefined): ConsentRegion {
  if (!tz) return "opt-in";
  if (tz.startsWith("Europe/") || OPT_IN_TZ_EXTRAS.has(tz)) return "opt-in";
  return "opt-out";
}

export function getConsentRegion(): ConsentRegion {
  try {
    return regionFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return "opt-in";
  }
}

/** Global Privacy Control — a browser-level "do not sell/share" opt-out signal
 *  (CPPA enforces honoring it in California). Denies Advertising by default;
 *  an explicit user grant recorded after the signal still wins. */
export function hasGpcSignal(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}
